import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';

import * as PusherTypes from 'pusher-js';

import { WebsocketService } from '@core/services/websocket/websocket.service';
import * as Objects from '@core/objects';

import { DashboardService } from './dashboard.service';
import { LibraryService } from './library.service';
import { ApprovalsService } from '@app/services/app/approvals.service';
import { ProjectsService } from './projects.service';
import { ProjectsFormsService } from './projects-forms.service';
import { VersionsViewService } from './versions.service';
import { App } from 'app/store/state/app/app.state';
import { AppService } from './app.service';
import { Session } from 'app/store/models/session.model';
import { UsersOnlineService } from './users-online.service';
import { AppCurrentSessionService } from './app-current-session.service';

// class SessionPackage {
//     constructor(private session: Session){
//         console.log("session ---->", session);
//     }

//     empty = () : boolean => this.session == null;

//     organization = () : Objects.Organization => this.session.current.organization;

//     user = () : Objects.User => this.session.current.user;

//     options = () : any => this.session.options;

//     configuration = (): any => this.session.configuration; 
// }

@Injectable({
    providedIn: 'root'
})
export class AppBehaviorService {
    private readonly TIMER_UPDATE_FEEDBACKS = 500;
    private usrChannel: PusherTypes.Channel;
    private orgChannel: PusherTypes.Channel;
    // private onlineChannel: PusherTypes.PresenceChannel;

    private sessionSubscriptor: Subscription;
    private organizationSubscriptor: Subscription;

    // private session: SessionPackage = null;
    private session: Session = null;
    private organization: number = null;

    constructor(
        private appSrv: AppService,
        private dashSrv: DashboardService,
        private libSrv: LibraryService,
        private approvalsSrv: ApprovalsService,
        private prjSrv: ProjectsService,
        private projectFormsSrv: ProjectsFormsService,
        private verSrv: VersionsViewService,
        private websocket: WebsocketService,
        private usrOnline: UsersOnlineService,
        private currentSessionSrv: AppCurrentSessionService
    ) {}
    
    start() {
        // this.appSubscriptor = this.appSrv.selectApp().subscribe((app: App) => this.init(app));
        this.sessionSubscriptor = this.appSrv.getSession().subscribe((session: Session) => this.init(session));
        
        this.appSrv.init();
    }

    end() {
        // unbind channels
        this.websocket.unbind(this.usrChannel);
        this.websocket.unbind(this.orgChannel);
        // this.websocket.unbind(this.onlineChannel);

        this.usrOnline.end();
        this.currentSessionSrv.unset();
        
        if(this.sessionSubscriptor != null)
            this.sessionSubscriptor.unsubscribe();

        if(this.organizationSubscriptor != null)
            this.organizationSubscriptor.unsubscribe();
        
        this.session = null;
    }

    private init(session: Session) {

        // first time
        if(session == null)
            return;
        
        // check if change org
        if(this.session !== null && this.session.current.organization === session.current.organization)
            return;
        
        this.session = session;

        // fix 2024/03/12 <rafael@twonas.com>
        // put pusher config in environment
        // let config = this.session.configuration;
        // this.websocket.connect(config.websocket.pusher.key, config.websocket.pusher.cluster);
        this.websocket.connect();

        this.websocket.unbind(this.usrChannel);
        this.usrChannel = this.websocket.addChannel('private-usr_'+this.session.current.user.id);

        this.websocket.unbind(this.orgChannel);
        this.orgChannel = this.websocket.addChannel('private-org_'+this.session.current.organization.id);

        this.usrOnline.start(session);
        this.currentSessionSrv.set(session);

        this.general();
        this.dashboard();
        this.library();
        this.approvals();

        // launch Request and Version events listeners
        this.projects();
        this.artworks();

        console.log("App behavior initialized");

        // Websocket disconnect
        this.websocket.getState$().subscribe(state => {
            if(state.current == 'unavailable'){
                this.appSrv.disconnect();
            }
        });

        // online presence channel listeners


    }

    // private initOrganization(org: number) { 

    // }

    private general() {
        this.usrChannel.bind('pusher:subscription_error', status => this.appSrv.setHttpError(status));
    }

    private projects() {
        // bind project messages updated events
        this.orgChannel.bind('request.messages.updated', data => {
            this.prjSrv.updateMessages(+data.request);
        });

        this.orgChannel.bind('version.feedbacks.updated', data => {
            // update related versions with delay
            timer(this.TIMER_UPDATE_FEEDBACKS).subscribe(() => {
                this.prjSrv.updateFeedbacks(+data.version);
            });
        });

        this.orgChannel.bind('version.updated', data => {
            this.prjSrv.updateRelatedVersion(+data.version);
        });
    }

    private artworks() {
        // bind version updated events for several content
        // update version messages
        this.orgChannel.bind('version.messages.updated', data => {
            this.verSrv.updateMessages(+data.version);
        });
        // update version feedbacks but we need the user to maybe update the share for permissions
        this.orgChannel.bind('version.feedbacks.updated', data => {
            console.debug(" #BEHAV version.feedbacks.updated ====> ", data)

            timer(this.TIMER_UPDATE_FEEDBACKS).subscribe(() => {
                this.verSrv.updateVersionShareBubbles(+data.version, this.session.current.user);
            });
        });
        // dec-2023 update version shares for uplaod-version updated but we need the user to maybe update the share for permissions
        this.orgChannel.bind('version.upload-version.updated', data => {
            console.debug(" #BEHAV version.upload-version.updated data====> ", data);
            
            timer(this.TIMER_UPDATE_FEEDBACKS).subscribe(() => {
                this.verSrv.updateVersionShareBubbles(+data.version, this.session.current.user);
            });
            
        });
        
    }

    private dashboard() {
        // this.dashSrv.reset();
        this.dashSrv.loadWorkflows();
        this.dashSrv.start(this.session.options.custom.dashboard_search, "session");
        this.orgChannel.bind('request.updated', data => {
            this.dashSrv.updateProject(+data.request, data.source_event);
            this.projectFormsSrv.updateProjectForms(+data.request, data.source_event);
            this.prjSrv.update(+data.request, data.source_event);
        });
    }

    private library() {
        // this.libSrv.reset();
        this.libSrv.loadVersionsStatusAccess();
        // manage to bind version updated ws events (source_event is not coming)
        this.orgChannel.bind('version.updated', data => {
            this.libSrv.updateVersion(+data.version);
            this.verSrv.update(+data.version);
        });
        // update library feedbacks 
        this.orgChannel.bind('version.feedbacks.updated', data => {
            timer(this.TIMER_UPDATE_FEEDBACKS).subscribe(() => {
                this.libSrv.updateVersionFeedbacks(+data.version);
            });
        });
    }

    private approvals() {
        // manage to bind version updated ws events (source_event is not coming on new events)
        this.orgChannel.bind('version.updated', data => {
            this.approvalsSrv.updateVersion(+data.version, data.source_event);
            this.verSrv.update(+data.version);
        });
        // update approvals screen or remove the version if was archived.
        this.orgChannel.bind('version.feedbacks.updated', data => {
            timer(this.TIMER_UPDATE_FEEDBACKS).subscribe(() => {
                this.approvalsSrv.updateVersionFeedbacks(+data.version);
            });
        });
    }

    // dispatch change org
    changeOrganization(org: number)
    {
        this.dashSrv.reset();
        this.libSrv.reset();
        this.approvalsSrv.reset();
        this.appSrv.changeOrganization(org);
    }

}